import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import apiFetch from "../../../shared/services/apiClient";
import { useToast, ConfirmModal } from "../../../shared/components/SharedUI";
import { getReservaciones, revisarReservacion, cambiarFechaReservacion, deleteReservacion } from "../services/portalService";
import { overlayClose } from "../../../shared/utils/overlayClose";
import NuevaReservacionModal from "../components/NuevaReservacionModal";
import DatosPagoFields, { pagoInicial, validarPago, pagoPayload } from "../components/DatosPagoFields";
import { TableToolbar, FilterField, FilterSelect, FilterInput } from "../../finance/shared/TableToolbar";
import styles from "../../finance/invoices/styles/invoice.module.css";

// ── Responsive hook ───────────────────────────────────────────────────────────
function useBreakpoint() {
  const get = () => window.innerWidth < 640 ? "mobile" : window.innerWidth < 1024 ? "tablet" : "desktop";
  const [bp, setBp] = useState(get);
  useEffect(() => {
    const fn = () => setBp(get());
    window.addEventListener("resize", fn);
    return () => window.removeEventListener("resize", fn);
  }, []);
  return bp;
}

const PAGE_SIZE = 10;

const ESTADOS = {
  PENDIENTE: { bg: "rgba(245,158,11,0.1)",  border: "rgba(245,158,11,0.3)",  text: "#f59e0b",  icon: "fa-clock" },
  APROBADA:  { bg: "rgba(16,185,129,0.1)",  border: "rgba(16,185,129,0.3)",  text: "#10b981",  icon: "fa-circle-check" },
  RECHAZADA: { bg: "rgba(248,113,113,0.1)", border: "rgba(248,113,113,0.3)", text: "#f87171",  icon: "fa-circle-xmark" },
  CANCELADA: { bg: "rgba(156,163,175,0.1)", border: "rgba(156,163,175,0.3)", text: "#9ca3af",  icon: "fa-ban" },
};

function EstadoBadge({ estado }) {
  const c = ESTADOS[estado] || ESTADOS.PENDIENTE;
  return (
    <span style={{
      background: c.bg, border: `1px solid ${c.border}`, color: c.text,
      borderRadius: 20, padding: "4px 10px", fontSize: 11, fontWeight: 700,
      display: "inline-flex", alignItems: "center", gap: 4, whiteSpace: "nowrap",
    }}>
      <i className={`fa-solid ${c.icon}`} style={{ fontSize: 9 }} />
      {estado}
    </span>
  );
}

function StatCard({ icon, label, value, color = "#f97316" }) {
  return (
    <div style={{
      background: "var(--bg-card)", border: "1px solid var(--border-card)",
      borderRadius: 12, padding: "16px 20px", display: "flex",
      alignItems: "center", gap: 14, flex: "1 1 130px", minWidth: 0,
    }}>
      <div style={{
        width: 40, height: 40, borderRadius: 10, flexShrink: 0,
        background: `${color}18`, border: `1px solid ${color}30`,
        display: "flex", alignItems: "center", justifyContent: "center",
        color, fontSize: 16,
      }}>
        <i className={`fa-solid ${icon}`} />
      </div>
      <div>
        <div style={{ fontSize: 22, fontWeight: 800, color: "var(--text-primary)", lineHeight: 1 }}>{value}</div>
        <div style={{ fontSize: 11, color: "var(--text-secondary)", marginTop: 3, fontWeight: 500, whiteSpace: "nowrap" }}>{label}</div>
      </div>
    </div>
  );
}

function Pagination({ page, total, onChange }) {
  const pages = Math.ceil(total / PAGE_SIZE);
  if (pages <= 1) return null;
  const btn = (disabled, onClick, children) => (
    <button onClick={onClick} disabled={disabled} style={{
      padding: "6px 12px", borderRadius: 8, border: "1px solid var(--border-card)",
      background: "var(--bg-card)", color: disabled ? "var(--text-secondary)" : "var(--text-primary)",
      cursor: disabled ? "default" : "pointer", fontSize: 13, opacity: disabled ? 0.4 : 1,
    }}>
      {children}
    </button>
  );
  return (
    <div style={{
      display: "flex", justifyContent: "space-between", alignItems: "center",
      padding: "12px 16px", borderTop: "1px solid var(--border-card)",
    }}>
      <span style={{ fontSize: 12, color: "var(--text-secondary)" }}>
        {(page - 1) * PAGE_SIZE + 1}-{Math.min(page * PAGE_SIZE, total)} de {total}
      </span>
      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        {btn(page === 1, () => onChange(page - 1), <i className="fa-solid fa-chevron-left" />)}
        <span style={{ padding: "5px 12px", borderRadius: 8, fontSize: 13, background: "#f97316", color: "#fff", fontWeight: 700 }}>
          {page} / {pages}
        </span>
        {btn(page === pages, () => onChange(page + 1), <i className="fa-solid fa-chevron-right" />)}
      </div>
    </div>
  );
}

const labelSt = { fontSize: 11, fontWeight: 700, color: "var(--text-secondary)", display: "block", marginBottom: 5, textTransform: "uppercase", letterSpacing: 0.5 };
const fldStyle = (extra = {}) => ({
  width: "100%", boxSizing: "border-box", padding: "9px 12px", borderRadius: 8,
  border: "1px solid var(--border-card)", background: "var(--bg-input, var(--hover-bg))",
  color: "var(--text-primary)", fontSize: 13, outline: "none", ...extra,
});

// Definido a nivel de módulo (no adentro de AdminReservaciones): si va adentro
// del componente, React lo recrea en cada render y trata al modal como un
// componente distinto en cada tecleo — lo desmonta y remonta, y el input de
// texto pierde el foco después de cada letra.
function ModalOverlay({ zIndex = 1000, width = 500, onClose, children }) {
  return (
    <div {...overlayClose(onClose)} style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)",
      backdropFilter: "blur(4px)", zIndex,
      display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: "var(--bg-card)", border: "1px solid var(--border-card)",
        borderRadius: 16, width: `min(${width}px, 96vw)`,
        boxShadow: "0 32px 80px rgba(0,0,0,0.6)",
        maxHeight: "92vh", overflowY: "auto",
      }}>
        {children}
      </div>
    </div>
  );
}

export default function AdminReservaciones() {
  const { t } = useTranslation();
  const { showToast, toastEl } = useToast();
  const bp = useBreakpoint();
  const isMobile = bp === "mobile";

  const [reservaciones, setReservaciones] = useState([]);
  const [filiales,      setFiliales]      = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [filtro,        setFiltro]        = useState("PENDIENTE");
  const [modal,         setModal]         = useState(null);
  const [notas,         setNotas]         = useState("");
  const [saving,        setSaving]        = useState(false);
  const [pago,          setPago]          = useState(pagoInicial);
  const [revErr,        setRevErr]        = useState("");
  const [fechaModal,    setFechaModal]    = useState(null);
  const [nuevaFecha,    setNuevaFecha]    = useState("");
  const [savingFecha,   setSavingFecha]   = useState(false);
  const [filterFilial,  setFilterFilial]  = useState("");
  const [filterDesde,   setFilterDesde]   = useState("");
  const [filterHasta,   setFilterHasta]   = useState("");
  const [page,          setPage]          = useState(1);
  const [confirmDel,    setConfirmDel]    = useState(null); // reservación a eliminar
  const [deleting,      setDeleting]      = useState(false);
  const [nuevaOpen,     setNuevaOpen]     = useState(false);

  async function handleEliminar() {
    if (!confirmDel) return;
    setDeleting(true);
    try {
      await deleteReservacion(confirmDel.id);
      showToast("Reservación eliminada", true);
      setConfirmDel(null);
      load();
    } catch (e) {
      showToast(e.message, false);
      setConfirmDel(null);
    } finally { setDeleting(false); }
  }

  function load() {
    getReservaciones().then(d => setReservaciones(d.results || d)).catch(() => {}).finally(() => setLoading(false));
  }
  useEffect(() => {
    load();
    apiFetch("/api/residents/filial/").then(r => r.json()).then(d => setFiliales(d.results || d)).catch(() => {});
  }, []);

  // Si un residente hace una reserva nueva mientras estamos acá, se actualiza
  // la lista sola en vez de esperar a que alguien la recargue a mano.
  useEffect(() => {
    function onDataChanged(e) {
      if (e.detail?.tipo === "RESERVA") load();
    }
    window.addEventListener("portal:data-changed", onDataChanged);
    return () => window.removeEventListener("portal:data-changed", onDataChanged);
  }, []);

  function abrirRevision(r) {
    setModal(r); setNotas(""); setRevErr("");
    // La fecha de pago arranca en hoy; la cuenta y el método se conservan de
    // la última revisión porque casi siempre es el mismo banco.
    setPago(p => ({ ...pagoInicial(), metodo_pago: p.metodo_pago, cuenta_bancaria: p.cuenta_bancaria }));
  }
  async function handleRechazar() {
    setSaving(true); setRevErr("");
    try {
      await revisarReservacion(modal.id, { estado: "RECHAZADA", notas_admin: notas });
      showToast(t("portal.reservaRechazada"), false);
      setModal(null); setNotas(""); load();
    } catch { setRevErr(t("common.errorGuardar")); }
    finally { setSaving(false); }
  }
  async function handleAprobar() {
    const e = validarPago(pago);
    if (e) { setRevErr(e); return; }
    setSaving(true); setRevErr("");
    try {
      await revisarReservacion(modal.id, { estado: "APROBADA", notas_admin: notas, ...pagoPayload(pago) });
      showToast(t("portal.reservaAprobada"), true);
      setModal(null); setNotas(""); load();
    } catch (err) {
      try { const d = JSON.parse(err.message); setRevErr(Object.values(d).flat().join(". ")); }
      catch { setRevErr(err.message || t("common.errorGuardar")); }
    } finally { setSaving(false); }
  }
  async function handleCambiarFecha() {
    if (!nuevaFecha) return;
    setSavingFecha(true);
    try {
      await cambiarFechaReservacion(fechaModal.id, nuevaFecha);
      showToast("Fecha actualizada", true);
      setFechaModal(null); load();
    } catch { showToast(t("common.errorGuardar"), false); }
    finally { setSavingFecha(false); }
  }

  const pendingCount = reservaciones.filter(r => r.estado === "PENDIENTE").length;

  const filtered = reservaciones.filter(r => {
    if (filtro !== "TODAS" && r.estado !== filtro) return false;
    if (filterFilial && r.filial_nombre !== filiales.find(f => String(f.id) === filterFilial)?.idFilial) return false;
    if (filterDesde && r.fecha < filterDesde) return false;
    if (filterHasta && r.fecha > filterHasta) return false;
    return true;
  });
  const paged = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const FILTROS = ["PENDIENTE", "APROBADA", "RECHAZADA", "TODAS"];

  return (
    <div className="portal-admin">
      {toastEl}
      <ConfirmModal
        open={!!confirmDel}
        name={confirmDel ? `${confirmDel.area_nombre} · ${confirmDel.filial_nombre} · ${confirmDel.fecha}` : ""}
        onConfirm={handleEliminar}
        onCancel={() => setConfirmDel(null)}
        loading={deleting}
      />

      {/* Breadcrumb */}
      <span className={styles.inactive}>
        <Link to="/">
          <span className={styles.inactive}>Portal Admin /</span>
        </Link>
        <span className={styles.active}> {t("portal.reservaciones")}</span>
      </span>

      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", gap: 12, flexWrap: "wrap" }}>
        <div>
          <h2 className="mt-2 p-0 title">
            {t("portal.reservaciones")}
          </h2>
          <p className="mb-3" style={{ color: "var(--text-muted)" }}>
            {t("portal.adminReservDesc")}
          </p>
        </div>
        <button onClick={() => setNuevaOpen(true)} style={{
          background: "#f97316", color: "#fff", border: "none", borderRadius: 8,
          padding: "9px 16px", cursor: "pointer", fontWeight: 700, fontSize: 13,
          display: "flex", alignItems: "center", gap: 7,
          boxShadow: "0 2px 8px rgba(249,115,22,0.3)",
        }}>
          <i className="fa-solid fa-calendar-plus" /> Nueva reservación
        </button>
      </div>

      {/* Stats */}
      {!loading && reservaciones.length > 0 && (
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 22 }}>
          <StatCard icon="fa-calendar-days"  label="Total"      value={reservaciones.length}                                          color="#f97316" />
          <StatCard icon="fa-clock"          label="Pendientes" value={reservaciones.filter(r => r.estado === "PENDIENTE").length}    color="#f59e0b" />
          <StatCard icon="fa-circle-check"   label="Aprobadas"  value={reservaciones.filter(r => r.estado === "APROBADA").length}     color="#10b981" />
          <StatCard icon="fa-circle-xmark"   label="Rechazadas" value={reservaciones.filter(r => r.estado === "RECHAZADA").length}    color="#ef4444" />
        </div>
      )}

      {/* Tab filters */}
      <div style={{ display: "flex", gap: 8, marginBottom: 14, flexWrap: "wrap" }}>
        {FILTROS.map(f => (
          <button key={f} onClick={() => { setFiltro(f); setPage(1); }} style={{
            padding: "7px 16px", borderRadius: 20, cursor: "pointer",
            fontWeight: 600, fontSize: 13, border: "none",
            background: filtro === f ? "#f97316" : "var(--hover-bg)",
            color: filtro === f ? "#fff" : "var(--text-secondary)",
            display: "flex", alignItems: "center", gap: 6,
            boxShadow: filtro === f ? "0 2px 8px rgba(249,115,22,0.3)" : "none",
            transition: "all 0.15s",
          }}>
            {f === "TODAS" ? t("portal.todas") : f}
            {f === "PENDIENTE" && pendingCount > 0 && (
              <span style={{
                background: "#f87171", color: "#fff",
                borderRadius: 99, padding: "1px 6px", fontSize: 10, fontWeight: 800,
              }}>
                {pendingCount}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Card with filters + list */}
      <div style={{
        background: "var(--bg-card)", border: "1px solid var(--border-card)",
        borderRadius: 8, overflow: "hidden", overflowX: "auto",
      }}>
        <TableToolbar
          loading={loading}
          activeFilterCount={[filterFilial, filterDesde, filterHasta].filter(Boolean).length}
          onClearFilters={() => { setFilterFilial(""); setFilterDesde(""); setFilterHasta(""); setPage(1); }}
          rightActions={
            <span style={{
              fontSize: "0.72rem", color: "var(--text-secondary)",
              background: "var(--hover-bg)", borderRadius: 20, padding: "4px 10px",
              fontWeight: 600, whiteSpace: "nowrap",
            }}>
              {filtered.length} reservación{filtered.length !== 1 ? "es" : ""}
            </span>
          }
          filters={
            <>
              <FilterField label="Filial">
                <FilterSelect value={filterFilial} onChange={(e) => { setFilterFilial(e.target.value); setPage(1); }}>
                  <option value="">Todas las filiales</option>
                  {filiales.map((f) => <option key={f.id} value={f.id}>Filial {f.idFilial}</option>)}
                </FilterSelect>
              </FilterField>
              <FilterField label="Desde">
                <FilterInput type="date" value={filterDesde} onChange={(e) => { setFilterDesde(e.target.value); setPage(1); }} />
              </FilterField>
              <FilterField label="Hasta">
                <FilterInput type="date" value={filterHasta} onChange={(e) => { setFilterHasta(e.target.value); setPage(1); }} />
              </FilterField>
            </>
          }
        />

        {/* List */}
        {loading ? (
          <div style={{ padding: 48, textAlign: "center", color: "var(--text-secondary)" }}>
            <i className="fa-solid fa-circle-notch fa-spin" style={{ fontSize: 24, marginBottom: 12, display: "block" }} />
            Cargando reservaciones…
          </div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: "48px 24px", textAlign: "center" }}>
            <div style={{
              width: 60, height: 60, borderRadius: "50%",
              background: "rgba(249,115,22,0.08)", border: "1px solid rgba(249,115,22,0.2)",
              display: "flex", alignItems: "center", justifyContent: "center",
              margin: "0 auto 16px", color: "#f97316", fontSize: 24,
            }}>
              <i className="fa-solid fa-calendar-xmark" />
            </div>
            <p style={{ margin: 0, fontWeight: 700, color: "var(--text-primary)", fontSize: 15 }}>
              {reservaciones.length === 0 ? t("portal.sinReservaciones") : "Sin resultados"}
            </p>
            <p style={{ margin: "6px 0 0", color: "var(--text-secondary)", fontSize: 13 }}>
              {reservaciones.length === 0 ? "No hay reservaciones aún." : "Probá cambiando los filtros."}
            </p>
          </div>
        ) : (
          <>
            {paged.map((r, i) => (
              <div key={r.id} style={{
                padding: isMobile ? "14px 14px" : "16px 18px",
                borderBottom: i < paged.length - 1 ? "1px solid var(--border-card)" : "none",
                transition: "background 0.12s",
              }}
                onMouseEnter={e => e.currentTarget.style.background = "var(--hover-bg)"}
                onMouseLeave={e => e.currentTarget.style.background = "transparent"}
              >
                {/* Top row */}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, marginBottom: 8 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                      <p style={{ margin: 0, fontWeight: 700, fontSize: 15, color: "var(--text-primary)" }}>
                        {r.area_nombre}
                      </p>
                      <EstadoBadge estado={r.estado} />
                    </div>
                    <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginTop: 5 }}>
                      <span style={{ fontSize: 12, color: "var(--text-secondary)", display: "flex", alignItems: "center", gap: 4 }}>
                        <i className="fa-solid fa-building" style={{ fontSize: 10, opacity: 0.6 }} />
                        {r.filial_nombre}
                      </span>
                      <span style={{ fontSize: 12, color: "var(--text-secondary)", display: "flex", alignItems: "center", gap: 4 }}>
                        <i className="fa-solid fa-calendar" style={{ fontSize: 10, opacity: 0.6 }} />
                        {r.fecha}
                      </span>
                      <span style={{ fontSize: 12, color: "var(--text-secondary)", display: "flex", alignItems: "center", gap: 4 }}>
                        <i className="fa-solid fa-clock" style={{ fontSize: 10, opacity: 0.6 }} />
                        {r.horario_nombre}
                      </span>
                      {r.usuario_nombre && (
                        <span style={{ fontSize: 12, color: "var(--text-secondary)", display: "flex", alignItems: "center", gap: 4 }}>
                          <i className="fa-solid fa-user" style={{ fontSize: 10, opacity: 0.6 }} />
                          {r.usuario_nombre}
                        </span>
                      )}
                    </div>
                    {r.factura_numero && (
                      <div style={{ marginTop: 5, display: "flex", gap: 8, flexWrap: "wrap" }}>
                        <span style={{
                          background: "rgba(249,115,22,0.08)", border: "1px solid rgba(249,115,22,0.2)",
                          color: "#f97316", borderRadius: 6, padding: "2px 8px", fontSize: 11, fontWeight: 600,
                        }}>
                          <i className="fa-solid fa-file-invoice" style={{ marginRight: 4 }} />
                          {r.factura_numero}
                        </span>
                        {r.recibo_numero && (
                          <span style={{
                            background: "rgba(16,185,129,0.08)", border: "1px solid rgba(16,185,129,0.2)",
                            color: "#10b981", borderRadius: 6, padding: "2px 8px", fontSize: 11, fontWeight: 600,
                          }}>
                            {r.recibo_numero}
                          </span>
                        )}
                        {r.fecha_pago && (
                          <span style={{ fontSize: 11, color: "var(--text-secondary)" }}>
                            Pagado: {r.fecha_pago}
                          </span>
                        )}
                      </div>
                    )}
                    {r.notas && (
                      <p style={{ margin: "6px 0 0", fontSize: 12, color: "var(--text-secondary)", fontStyle: "italic" }}>
                        <i className="fa-solid fa-quote-left" style={{ marginRight: 4, opacity: 0.5 }} />
                        {r.notas}
                      </p>
                    )}
                    {r.notas_admin && (
                      <p style={{ margin: "4px 0 0", fontSize: 12, color: "var(--text-secondary)" }}>
                        <i className="fa-solid fa-comment" style={{ marginRight: 4, color: "#f97316" }} />
                        {r.notas_admin}
                      </p>
                    )}
                  </div>
                </div>

                {/* Action buttons */}
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  {r.comprobante_url && (
                    <a href={r.comprobante_url} target="_blank" rel="noreferrer" style={{
                      background: "rgba(59,130,246,0.08)", border: "1px solid rgba(59,130,246,0.25)",
                      color: "#3b82f6", borderRadius: 8, padding: "6px 12px",
                      fontWeight: 600, fontSize: 12, textDecoration: "none",
                      display: "flex", alignItems: "center", gap: 5,
                    }}>
                      <i className="fa-solid fa-file" /> Ver comprobante
                    </a>
                  )}
                  {r.estado === "PENDIENTE" && (
                    <button onClick={() => abrirRevision(r)} style={{
                      background: "rgba(249,115,22,0.1)", border: "1px solid rgba(249,115,22,0.3)",
                      color: "#f97316", borderRadius: 8, padding: "6px 14px",
                      cursor: "pointer", fontWeight: 600, fontSize: 13,
                      display: "flex", alignItems: "center", gap: 5,
                    }}>
                      <i className="fa-solid fa-clipboard-check" /> {t("portal.revisar")}
                    </button>
                  )}
                  {r.estado === "APROBADA" && (
                    <button onClick={() => { setFechaModal(r); setNuevaFecha(r.fecha); }} style={{
                      background: "rgba(99,102,241,0.08)", border: "1px solid rgba(99,102,241,0.25)",
                      color: "#818cf8", borderRadius: 8, padding: "6px 12px",
                      cursor: "pointer", fontWeight: 600, fontSize: 12,
                      display: "flex", alignItems: "center", gap: 5,
                    }}>
                      <i className="fa-solid fa-calendar-pen" /> Cambiar fecha
                    </button>
                  )}
                  <button onClick={() => setConfirmDel(r)} title="Eliminar reservación" style={{
                    background: "rgba(248,113,113,0.08)", border: "1px solid rgba(248,113,113,0.25)",
                    color: "#f87171", borderRadius: 8, padding: "6px 12px",
                    cursor: "pointer", fontWeight: 600, fontSize: 12,
                    display: "flex", alignItems: "center", gap: 5,
                  }}>
                    <i className="fa-solid fa-trash" /> {t("common.eliminar")}
                  </button>
                </div>
              </div>
            ))}
            <Pagination page={page} total={filtered.length} onChange={setPage} />
          </>
        )}
      </div>

      {/* ── Nueva reservación (a nombre de una filial) ── */}
      {nuevaOpen && (
        <NuevaReservacionModal
          filiales={filiales}
          onClose={() => setNuevaOpen(false)}
          onSaved={(r) => {
            setNuevaOpen(false);
            showToast(r.estado === "APROBADA" ? "Reservación registrada y aprobada" : "Reservación registrada", true);
            setFiltro(r.estado === "APROBADA" ? "APROBADA" : "PENDIENTE");
            setPage(1);
            load();
          }}
        />
      )}

      {/* ── Revisar: aprobar (con datos del pago) o rechazar ── */}
      {modal && (
        <ModalOverlay width={640} onClose={() => !saving && setModal(null)}>
          <div style={{ padding: "22px 24px 18px", borderBottom: "1px solid var(--border-card)", display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
            <div>
              <h3 style={{ margin: 0, fontWeight: 700, fontSize: 16, color: "var(--text-primary)" }}>
                {t("portal.revisarReserva")}
              </h3>
              <p style={{ margin: "4px 0 0", fontSize: 12, color: "var(--text-secondary)" }}>
                {modal.area_nombre} · {modal.filial_nombre} · {modal.fecha} · {modal.horario_nombre}
              </p>
            </div>
            <button onClick={() => setModal(null)} style={{ background: "none", border: "none", color: "var(--text-muted)", cursor: "pointer", fontSize: 18 }}>
              <i className="fa-solid fa-xmark" />
            </button>
          </div>
          <div style={{ padding: "20px 24px", display: "flex", flexDirection: "column", gap: 16 }}>
            {(modal.notas || modal.comprobante_url) && (
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {modal.notas && (
                  <p style={{ margin: 0, fontSize: 13, color: "var(--text-secondary)", fontStyle: "italic" }}>
                    <i className="fa-solid fa-quote-left" style={{ marginRight: 6, opacity: 0.5 }} />
                    {modal.notas}
                  </p>
                )}
                {modal.comprobante_url && (
                  <a href={modal.comprobante_url} target="_blank" rel="noreferrer" style={{
                    display: "inline-flex", alignItems: "center", gap: 6, alignSelf: "flex-start",
                    background: "rgba(59,130,246,0.08)", border: "1px solid rgba(59,130,246,0.25)",
                    color: "#3b82f6", borderRadius: 8, padding: "8px 14px",
                    fontSize: 13, fontWeight: 600, textDecoration: "none",
                  }}>
                    <i className="fa-solid fa-file" /> Ver comprobante adjunto
                  </a>
                )}
              </div>
            )}
            <div>
              <label style={labelSt}>{t("portal.notasAdmin")} (opcional)</label>
              <textarea value={notas} onChange={e => setNotas(e.target.value)} rows={2}
                placeholder={t("portal.motivoPlaceholder")}
                style={fldStyle({ resize: "vertical" })}
              />
            </div>

            <div style={{ border: "1px solid var(--border-card)", borderRadius: 10, padding: "14px 16px" }}>
              <p style={{ margin: "0 0 4px", fontSize: 13, fontWeight: 700, color: "var(--text-primary)" }}>
                <i className="fa-solid fa-receipt" style={{ marginRight: 6, color: "#f97316" }} />
                Datos del pago
              </p>
              <p style={{ margin: "0 0 14px", fontSize: 12, color: "var(--text-secondary)" }}>
                Solo se usan al aprobar: generan la factura y el recibo de la filial en esta cuenta bancaria.
              </p>
              <DatosPagoFields
                pago={pago}
                onChange={p => { setPago(prev => ({ ...prev, ...p })); setRevErr(""); }}
                area={{ precio_colones: modal.area_precio_colones, precio_dolares: modal.area_precio_dolares }}
              />
            </div>

            {revErr && (
              <div style={{
                background: "rgba(248,113,113,0.08)", border: "1px solid rgba(248,113,113,0.3)",
                borderLeft: "3px solid #f87171", borderRadius: 8,
                padding: "10px 14px", color: "#f87171", fontSize: 13,
              }}>
                {revErr}
              </div>
            )}

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <button onClick={handleRechazar} disabled={saving} style={{
                padding: 12, borderRadius: 8, border: "1px solid rgba(248,113,113,0.4)",
                background: "rgba(248,113,113,0.1)", color: "#f87171",
                fontWeight: 700, fontSize: 14, cursor: saving ? "not-allowed" : "pointer",
                display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
              }}>
                <i className="fa-solid fa-xmark" /> {t("portal.rechazar")}
              </button>
              <button onClick={handleAprobar} disabled={saving} style={{
                padding: 12, borderRadius: 8, border: "none",
                background: "#22c55e", color: "#fff",
                fontWeight: 700, fontSize: 14, cursor: saving ? "not-allowed" : "pointer",
                display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
              }}>
                {saving
                  ? <><i className="fa-solid fa-circle-notch fa-spin" /> Procesando…</>
                  : <><i className="fa-solid fa-check" /> {t("portal.aprobar")}</>}
              </button>
            </div>
          </div>
        </ModalOverlay>
      )}

      {/* ── Date change modal ── */}
      {fechaModal && (
        <ModalOverlay onClose={() => setFechaModal(null)}>
          <div style={{ padding: "22px 24px 18px", borderBottom: "1px solid var(--border-card)", display: "flex", justifyContent: "space-between" }}>
            <div>
              <h3 style={{ margin: 0, fontWeight: 700, fontSize: 16, color: "var(--text-primary)" }}>Cambiar fecha</h3>
              <p style={{ margin: "4px 0 0", fontSize: 12, color: "var(--text-secondary)" }}>
                {fechaModal.area_nombre} · {fechaModal.filial_nombre}
              </p>
            </div>
            <button onClick={() => setFechaModal(null)} style={{ background: "none", border: "none", color: "var(--text-muted)", cursor: "pointer", fontSize: 18 }}>
              <i className="fa-solid fa-xmark" />
            </button>
          </div>
          <div style={{ padding: "20px 24px" }}>
            <label style={labelSt}>Nueva fecha</label>
            <input type="date" value={nuevaFecha} onChange={e => setNuevaFecha(e.target.value)}
              style={{ ...fldStyle(), marginBottom: 16 }} />
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button onClick={() => setFechaModal(null)} style={{
                padding: "9px 18px", borderRadius: 8, border: "1px solid var(--border-card)",
                background: "transparent", color: "var(--text-secondary)", cursor: "pointer", fontSize: 14,
              }}>
                {t("common.cancelar")}
              </button>
              <button onClick={handleCambiarFecha} disabled={savingFecha} style={{
                padding: "9px 22px", borderRadius: 8, border: "none",
                background: "#f97316", color: "#fff", fontWeight: 700, fontSize: 14,
                cursor: savingFecha ? "not-allowed" : "pointer",
                display: "flex", alignItems: "center", gap: 6,
              }}>
                {savingFecha
                  ? <><i className="fa-solid fa-circle-notch fa-spin" /> Guardando…</>
                  : <><i className="fa-solid fa-floppy-disk" /> {t("common.guardar")}</>}
              </button>
            </div>
          </div>
        </ModalOverlay>
      )}
    </div>
  );
}
